import { z } from 'zod';
import { refersTo, refersToSubject } from '../common/refers-to';

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const MailboxProviderSchema = z.enum(['GOOGLE', 'MICROSOFT', 'IMAP']);

/**
 * Posteingang row ORDER. `unread-first` floats unread mail above the rest — a
 * sort, not the `unread` filter, which hides read mail entirely. Ignored for the
 * Postausgang, which has no unread state.
 */
export const MailboxListViewSchema = z.enum(['default', 'unread-first']);
export type MailboxListView = z.infer<typeof MailboxListViewSchema>;
export type MailboxProvider = z.infer<typeof MailboxProviderSchema>;

export const MailboxConnectionStatusSchema = z.enum(['ACTIVE', 'EXPIRED']);
export type MailboxConnectionStatus = z.infer<typeof MailboxConnectionStatusSchema>;

/**
 * Why the member is being asked to reconnect (IMM-530.7). `EXPIRED` is a fault to
 * fix; `SWITCHOVER` is an invitation on a still-ACTIVE broker-backed connection,
 * which keeps serving until they act. Presentational only — it never changes
 * which stack serves the grant. Absent = nothing to prompt.
 */
export const MailboxReconnectReasonSchema = z.enum(['EXPIRED', 'SWITCHOVER']);
export type MailboxReconnectReason = z.infer<typeof MailboxReconnectReasonSchema>;

export const ImapProviderSchema = z.enum([
  'GMX',
  'WEB_DE',
  'T_ONLINE',
  'IONOS',
  'STRATO',
  'MAILBOX_ORG',
  'POSTEO',
  'FREENET',
]);
export type ImapProvider = z.infer<typeof ImapProviderSchema>;

// A DNS name or an IPv4 literal; IPv6 literals are not accepted.
export const IMAP_HOST_PATTERN =
  /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$|^\d{1,3}(\.\d{1,3}){3}$/i;
export const ImapServerSchema = z.object({
  host: z.string().trim().min(1).max(253).regex(IMAP_HOST_PATTERN),
  port: z.number().int().min(1).max(65535),
});
export type ImapServer = z.infer<typeof ImapServerSchema>;

export const ImapConnectionSummarySchema = z.object({
  provider: ImapProviderSchema.nullable(),
  username: z.string(),
  imap: ImapServerSchema,
  smtp: ImapServerSchema.nullable(),
});
export type ImapConnectionSummary = z.infer<typeof ImapConnectionSummarySchema>;

export const GetMailboxConnectionPathParamsSchema = WorkspaceIdPathParamsSchema;
export const MailboxConnectionResponseSchema = z.object({
  connected: z.boolean(),
  email: z.string().nullable(),
  provider: MailboxProviderSchema.nullable(),
  status: MailboxConnectionStatusSchema.nullable(),
  reconnectReason: MailboxReconnectReasonSchema.optional(),
  // OUR `mailbox_grants.id` of the caller's personal mailbox — the id the account
  // filter takes. Absent when the caller has no connected mailbox.
  accountId: z.uuid().optional(),
  // What a NEW connection may choose on this deployment (IMM-1170.1 / IMM-1173.1).
  availableProviders: z.array(MailboxProviderSchema),
  // What the connected IMAP row names and what a reconnect pre-fills (IMM-1173.5/.8). Never the password.
  imap: ImapConnectionSummarySchema.optional(),
});
export type MailboxConnectionResponse = z.infer<typeof MailboxConnectionResponseSchema>;

export const InitiateMailboxConnectionPathParamsSchema = WorkspaceIdPathParamsSchema;
export const InitiateMailboxConnectionBodySchema = z.object({ provider: MailboxProviderSchema }).strict();
export type InitiateMailboxConnectionBody = z.infer<typeof InitiateMailboxConnectionBodySchema>;
export const InitiateMailboxConnectionResponseSchema = z.object({ authUrl: z.string() });

export const CompleteMailboxConnectionPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CompleteMailboxConnectionBodySchema = z
  // `state` is the signed token minted on connect and echoed back by the provider;
  // the backend verifies it binds this workspace/member/provider (OAuth CSRF guard).
  .object({ provider: MailboxProviderSchema, code: z.string().min(1), state: z.string().min(1) })
  .strict();
export type CompleteMailboxConnectionBody = z.infer<typeof CompleteMailboxConnectionBodySchema>;
export const CompleteMailboxConnectionResponseSchema = z.object({
  email: z.string(),
  status: MailboxConnectionStatusSchema,
});

export const ConnectImapMailboxPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ConnectImapMailboxBodySchema = z
  .object({
    email: z.email(),
    // Never trimmed: a password is taken as typed.
    password: z.string().min(1).max(1024),
    username: z.string().trim().min(1).max(320),
    imap: ImapServerSchema,
    smtp: ImapServerSchema,
  })
  .strict();
export type ConnectImapMailboxBody = z.infer<typeof ConnectImapMailboxBodySchema>;

export const ConnectImapCompanyMailboxBodySchema = z
  .object({
    email: z.email(),
    // Never trimmed: a password is taken as typed.
    password: z.string().min(1).max(1024),
    username: z.string().trim().min(1).max(320),
    imap: ImapServerSchema,
  })
  .strict();
export type ConnectImapCompanyMailboxBody = z.infer<typeof ConnectImapCompanyMailboxBodySchema>;

export const ReconnectImapCompanyMailboxBodySchema = ConnectImapCompanyMailboxBodySchema.omit({ email: true }).strict();
export type ReconnectImapCompanyMailboxBody = z.infer<typeof ReconnectImapCompanyMailboxBodySchema>;

export const DiscoverImapServerPathParamsSchema = WorkspaceIdPathParamsSchema;
export const DiscoverImapServerBodySchema = z.object({ email: z.email() }).strict();
export type DiscoverImapServerBody = z.infer<typeof DiscoverImapServerBodySchema>;
export const DiscoverImapServerResponseSchema = z.object({
  provider: ImapProviderSchema.nullable(),
  imap: ImapServerSchema.nullable(),
  smtp: ImapServerSchema.nullable(),
});
export type DiscoverImapServerResponse = z.infer<typeof DiscoverImapServerResponseSchema>;

export const DisconnectMailboxPathParamsSchema = WorkspaceIdPathParamsSchema;
export const DisconnectMailboxResponseSchema = z.object({}).strict();

// Thread-read API (IMM-233.1.2). Every read is workspace-scoped; the thread route
// keys on the raw `providerThreadId` the client carries in the timeline activity.
// `messageId` on the body / mark-read / attachment routes is the STABLE read-model
// message id (a UUID), never a provider id — the backend resolves it internally.
export const MailboxAccountKindSchema = z.enum(['PERSONAL', 'COMPANY']);

export const MailDirectionSchema = z.enum(['INBOUND', 'OUTBOUND']);
export type MailDirectionDto = z.infer<typeof MailDirectionSchema>;

export const MailBodyContentTypeSchema = z.enum(['html', 'text']);

// How the thread's contact was assigned (IMM-233.4): AUTOMATIC (auto-match),
// MANUAL (a member assigned it), or null when unassigned. Drives the web-app's
// "Kontakt zuordnen" vs "Zuordnung entfernen" affordance.
export const ThreadAssignmentSourceSchema = z.enum(['AUTOMATIC', 'MANUAL']);

export const EmailThreadViewSchema = z.object({
  id: z.uuid(),
  providerThreadId: z.string(),
  subject: z.string().nullable(),
  counterpartEmail: z.string().nullable(),
  // The counterpart's display name as it arrived on the email (From/To header),
  // independent of whether they are a saved contact. Null when the provider named none.
  // Optional (nullish) so a new client tolerates a not-yet-deployed backend that omits
  // it during a rollout — the client `safeParse`s the response body.
  counterpartName: z.string().nullish(),
  contactId: refersTo('contact', z.uuid()).nullable(),
  organizationId: refersTo('organization', z.uuid()).nullable(),
  assignmentSource: ThreadAssignmentSourceSchema.nullable(),
  lastMessageDirection: MailDirectionSchema,
  lastMessageAt: z.iso.datetime(),
  messageCount: z.number().int(),
  unreadCount: z.number().int(),
});
export type EmailThreadView = z.infer<typeof EmailThreadViewSchema>;

export const EmailMessageViewSchema = z.object({
  id: z.uuid(),
  providerMessageId: z.string(),
  direction: MailDirectionSchema,
  subject: z.string().nullable(),
  fromEmail: z.string().nullable(),
  toEmails: z.array(z.string()),
  snippet: z.string().nullable(),
  receivedAt: z.iso.datetime(),
  unread: z.boolean(),
  hasAttachments: z.boolean(),
});
export type EmailMessageView = z.infer<typeof EmailMessageViewSchema>;

export const GetEmailThreadPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  providerThreadId: z.string().min(1),
});
export const EmailThreadResponseSchema = z.object({
  thread: EmailThreadViewSchema,
  messages: z.array(EmailMessageViewSchema),
});
export type EmailThreadResponse = z.infer<typeof EmailThreadResponseSchema>;

export const GetEmailMessageBodyPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  messageId: z.uuid(),
});
// One attachment descriptor on a fetched message body (IMM-233 design 02) — metadata
// only (never bytes; ADR 0018). `id` is passed back to the download-attachment route.
export const EmailAttachmentSchema = z.object({
  id: z.string().min(1),
  filename: z.string(),
  contentType: z.string(),
  size: z.number().int(),
});
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;

export const EmailMessageBodyResponseSchema = z.object({
  contentType: MailBodyContentTypeSchema,
  body: z.string(),
  // Fetched live with the body so the thread view can render attachment cards.
  attachments: z.array(EmailAttachmentSchema),
});
export type EmailMessageBodyResponse = z.infer<typeof EmailMessageBodyResponseSchema>;

export const MarkEmailMessageReadPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  messageId: z.uuid(),
});
export const MarkEmailMessageReadResponseSchema = z.object({
  unread: z.literal(false),
  threadUnreadCount: z.number().int(),
});
export type MarkEmailMessageReadResponse = z.infer<typeof MarkEmailMessageReadResponseSchema>;

// Active mailbox sync (IMM-233): an on-demand pull of the acting member's most
// recent messages from the provider into the read-model, covering the delivery
// lag of Nylas basic-subscription webhooks. Member-implicit — syncs the caller's
// OWN mailbox, so `mailbox-thread-read` is the whole gate. `skipped` is true when
// nothing was pulled (no active grant, or within the per-grant cooldown).
export const SyncMailboxPathParamsSchema = WorkspaceIdPathParamsSchema;
export const SyncMailboxResponseSchema = z.object({
  syncedMessageCount: z.number().int(),
  skipped: z.boolean(),
});
export type SyncMailboxResponse = z.infer<typeof SyncMailboxResponseSchema>;

export const DownloadEmailAttachmentPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  messageId: z.uuid(),
  attachmentId: z.string().min(1),
});

export const AssignmentSourceSchema = z.enum(['AUTOMATIC', 'MANUAL']);
export type AssignmentSourceDto = z.infer<typeof AssignmentSourceSchema>;

export const EmailPlacementSubjectTypeSchema = z.enum(['CONTACT', 'LEAD', 'DEAL', 'PROPERTY']);
export type EmailPlacementSubjectTypeDto = z.infer<typeof EmailPlacementSubjectTypeSchema>;

export const EmailThreadPlacementSchema = z.object({
  subjectType: EmailPlacementSubjectTypeSchema,
  subjectId: refersToSubject('subjectType', z.uuid()),
  // Null when the subject no longer resolves or is outside the caller's visible Standorte.
  label: z.string().nullable(),
  source: AssignmentSourceSchema,
  needsReview: z.boolean(),
});
export type EmailThreadPlacement = z.infer<typeof EmailThreadPlacementSchema>;

// Posteingang / Postausgang list projections (IMM-233.3). Gmail-style chronological
// cross-contact lists over the SAME already-synced 233.1.1 read-model — no new
// source of truth, only a message-level list view. Every read is workspace + the
// acting member's OWN mailbox scoped (a member only ever lists their own inbox);
// the member-implicit `mailbox-thread-read` gate is the whole authorisation.
export const ListMailboxMessagesPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ListMailboxMessagesQuerySchema = z.object({
  // Posteingang = INBOUND (received), Postausgang = OUTBOUND (sent). Required — the
  // two lists differ only by this filter.
  direction: MailDirectionSchema,
  // Posteingang-only "nur ungelesen" filter. Ignored for OUTBOUND (sent mail has no
  // unread state) — the handler never applies it to outbound rows.
  unread: z.stringbool().optional(),
  // Narrow to one connected inbox by OUR `mailbox_grants.id` (see `accountId` on
  // the connection response) — never a vendor grant handle, which churns on
  // reconnect. Omitted lists every account.
  accountId: z.uuid().optional(),
  // Row order; omitted = `default` (newest first).
  view: MailboxListViewSchema.optional(),
  // Free-text search (design 03 "Im Posteingang suchen …"): a case-insensitive
  // substring match over the row's subject, counterpart address and snippet. Trimmed;
  // an empty query is treated as no filter.
  q: z.string().trim().min(1).max(200).optional(),
  // „Nur nicht zugeordnete": only rows of the caller's own personal Konversationen with no placement.
  // Company-mailbox rows never match (company mail is never recorded). Combines with every other filter.
  placement: z.enum(['UNPLACED']).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  // Opaque keyset cursor from a previous page's `nextCursor` — "Mehr laden".
  cursor: z.string().optional(),
});
export type ListMailboxMessagesQuery = z.infer<typeof ListMailboxMessagesQuerySchema>;

// One row on the Posteingang / Postausgang. Message-level (Gmail inbox rows are per
// message, newest first). `counterpart` is the sender (`fromEmail`) for an inbound
// row and the primary recipient (`toEmails[0]`) for an outbound row — the address
// shown on the row. `contactId` / `organizationId` are the owning thread's matched
// contact (the row's contact chip), null when unmatched. `providerThreadId` opens the
// row into the shared 233.1.3 conversation view.
export const MailboxMessageListItemSchema = z.object({
  messageId: z.uuid(),
  providerThreadId: z.string(),
  direction: MailDirectionSchema,
  subject: z.string().nullable(),
  counterpart: z.string().nullable(),
  // The counterpart's display name — the row's title, with `counterpart` as its
  // subtitle. Null when the provider reported no name, and for rows projected
  // before the name was persisted, so a reader always falls back to the address.
  counterpartName: z.string().nullable(),
  snippet: z.string().nullable(),
  contactId: refersTo('contact', z.uuid()).nullable(),
  // The matched contact's display name, for the row's contact chip. Resolved
  // across the module boundary at read time (never denormalised onto the thread),
  // so a renamed contact is correct on the next read. Null exactly when
  // `contactId` is null — an unassigned row shows the "not assigned" placeholder.
  contactName: z.string().nullable(),
  organizationId: refersTo('organization', z.uuid()).nullable(),
  // The owning thread's assignment source (IMM-233.4): drives the row's assign /
  // remove affordance. Null when the thread is unassigned.
  assignmentSource: ThreadAssignmentSourceSchema.nullable(),
  // Where the row's Konversation sits. Null for an unplaced personal Konversation and for every
  // company-mailbox row (whose chip still reads `contactId`).
  placement: EmailThreadPlacementSchema.nullable(),
  hasAttachments: z.boolean(),
  receivedAt: z.iso.datetime(),
  unread: z.boolean(),
  // The thread's `mailbox_grant_id`; null for rows projected before the column existed.
  accountId: z.uuid().nullable().optional(),
  accountKind: MailboxAccountKindSchema.optional(),
});
export type MailboxMessageListItem = z.infer<typeof MailboxMessageListItemSchema>;

export const ListMailboxMessagesResponseSchema = z.object({
  items: z.array(MailboxMessageListItemSchema),
  // Pass back as `?cursor=` for the next page; null when the list is exhausted.
  nextCursor: z.string().nullable(),
});
export type ListMailboxMessagesResponse = z.infer<typeof ListMailboxMessagesResponseSchema>;

export const CountUnplacedEmailThreadsPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CountUnplacedEmailThreadsResponseSchema = z.object({ count: z.number().int().nonnegative() });
export type CountUnplacedEmailThreadsResponse = z.infer<typeof CountUnplacedEmailThreadsResponseSchema>;

// Manual contact assignment (IMM-233.4). A member assigns an unassigned (or
// auto-matched) thread of their OWN mailbox to a workspace contact, marking it
// `MANUAL` so a later auto pass never overwrites it; optionally applies the same
// assignment to every OTHER thread from the same sender address; or removes an
// assignment (detaching the email from the contact's timeline). WRITE gate: the
// org-scoped `mailbox-send` capability (agent / assistant / owner-admin; not a
// Viewer) — the same capability the send surface uses, since assignment is the
// other member-owned mailbox write and must exclude a Viewer identically. The
// thread is addressed by the raw `providerThreadId` the client already holds; the
// backend resolves it to the caller's own thread and refuses a colleague's.
export const AssignEmailThreadContactPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  providerThreadId: z.string().min(1),
});
export const AssignEmailThreadContactBodySchema = z
  .object({
    contactId: z.uuid(),
    // "Alle E-Mails von diesem Absender zuordnen": also assign every OTHER of the
    // member's threads whose counterpart equals this thread's sender address,
    // except those already MANUALLY assigned to a DIFFERENT contact (non-destructive).
    applyToAllFromSender: z.boolean().default(false),
  })
  .strict();
export type AssignEmailThreadContactBody = z.infer<typeof AssignEmailThreadContactBodySchema>;
export const AssignEmailThreadContactResponseSchema = z.object({
  contactId: refersTo('contact', z.uuid()),
  organizationId: refersTo('organization', z.uuid()).nullable(),
  assignmentSource: AssignmentSourceSchema,
  // How many threads the assignment touched: 1 for the single thread, or the total
  // (this thread + the sender's other newly-assigned threads) for apply-to-all.
  assignedThreadCount: z.number().int().min(1),
});
export type AssignEmailThreadContactResponse = z.infer<typeof AssignEmailThreadContactResponseSchema>;

export const RemoveEmailThreadContactPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  providerThreadId: z.string().min(1),
});
export const RemoveEmailThreadContactResponseSchema = z
  .object({
    contactId: z.null(),
    assignmentSource: z.null(),
  })
  .strict();
export type RemoveEmailThreadContactResponse = z.infer<typeof RemoveEmailThreadContactResponseSchema>;

export const PlaceEmailThreadBodySchema = z
  .object({
    subjectType: EmailPlacementSubjectTypeSchema,
    subjectId: z.uuid(),
    applyToAllFromSender: z.boolean().default(false),
  })
  .strict()
  .refine((body) => !body.applyToAllFromSender || body.subjectType === 'CONTACT', { path: ['applyToAllFromSender'] });
export type PlaceEmailThreadBody = z.infer<typeof PlaceEmailThreadBodySchema>;
export const PlaceEmailThreadResponseSchema = z.object({
  placement: EmailThreadPlacementSchema,
  changed: z.boolean(),
  placedThreadCount: z.number().int(),
});
export type PlaceEmailThreadResponse = z.infer<typeof PlaceEmailThreadResponseSchema>;

export const RemoveEmailThreadPlacementResponseSchema = z.object({ removed: z.boolean() });
export type RemoveEmailThreadPlacementResponse = z.infer<typeof RemoveEmailThreadPlacementResponseSchema>;

export const EmailThreadPlacementSuggestionSchema = z.object({
  subjectType: z.enum(['DEAL', 'PROPERTY']),
  subjectId: refersToSubject('subjectType', z.uuid()),
  label: z.string(),
});
export type EmailThreadPlacementSuggestion = z.infer<typeof EmailThreadPlacementSuggestionSchema>;
export const EmailThreadPlacementSuggestionsResponseSchema = z.object({
  matchedContactId: refersTo('contact', z.uuid()).nullable(),
  suggestions: z.array(EmailThreadPlacementSuggestionSchema),
});
export type EmailThreadPlacementSuggestionsResponse = z.infer<typeof EmailThreadPlacementSuggestionsResponseSchema>;

// The personal thread read only: the company read keeps EmailThreadResponseSchema.
export const MailboxThreadResponseSchema = EmailThreadResponseSchema.extend({
  placement: EmailThreadPlacementSchema.nullable(),
});
export type MailboxThreadResponse = z.infer<typeof MailboxThreadResponseSchema>;

// Compose / reply / reply-all / forward → send (IMM-233.2). The member sends from
// their OWN connected mailbox (member-as-actor, ADR 0018) — never the brand domain.
// The send is a WRITE, gated by the org-scoped `communications:mailbox-send`
// capability (agent/assistant/owner-admin; not viewer).
export const MailAddressInputSchema = z.object({
  email: z.email(),
  name: z.string().max(120).optional(),
});
export type MailAddressInput = z.infer<typeof MailAddressInputSchema>;

// One outbound attachment. Bytes travel base64-encoded inside the JSON body
// (attachment-transport decision) — the backend decodes, enforces the combined
// 25 MB cap on the DECODED bytes before dispatch, and streams them to the
// provider. immoOS retains no copy (ADR 0018).
export const SendEmailAttachmentSchema = z.object({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1),
  contentBase64: z.string().min(1),
});
export type SendEmailAttachment = z.infer<typeof SendEmailAttachmentSchema>;

export const SendEmailPathParamsSchema = WorkspaceIdPathParamsSchema;

/**
 * The `href` the composer writes for a `{Link zur persönlichen Seite}` chip
 * (IMM-1177.7). The backend swaps it for a freshly issued IMM-1065 personal
 * link at send time; the link itself never reaches the browser.
 */
export const EMAIL_PERSONAL_LINK_HREF = 'immoos-personal-link:';
export const SendEmailBodySchema = z
  .object({
    // Forward-compat multi-account "Von" selector. V1 grants are single-per-member,
    // so the backend still resolves the caller's one active grant from
    // (workspaceId, memberId) — it never resolves an arbitrary grant. If supplied,
    // this id MUST equal the resolved grant; a contradicting value is rejected with
    // 422 communications.mailbox.grant-mismatch (never silently sent from another
    // mailbox), hardening the multi-account future.
    fromGrantId: z.string().min(1).optional(),
    to: z.array(MailAddressInputSchema).min(1),
    cc: z.array(MailAddressInputSchema).default([]),
    bcc: z.array(MailAddressInputSchema).default([]),
    // Empty subject is refused per-field on the composer; the backend permits "" so
    // the provider (which allows a blank subject) stays the single authority on it.
    subject: z.string().max(255),
    // Rich-text HTML body with the signature + quoted history already assembled by
    // the composer (the backend injects no signature — IMM-258 seam is FE-side).
    body: z.string(),
    // Carried on reply / reply-all / forward so the provider keeps the conversation
    // threaded. The RAW provider message id of the message being replied to.
    replyToProviderMessageId: z.string().min(1).optional(),
    attachments: z.array(SendEmailAttachmentSchema).default([]),
    // The Interessent whose personal page every `EMAIL_PERSONAL_LINK_HREF` anchor
    // in `body` links to (IMM-1177.7). Ignored when the body holds no such anchor.
    personalLinkProspectId: refersTo('prospect', z.uuid()).optional(),
  })
  .strict();
export type SendEmailBody = z.infer<typeof SendEmailBodySchema>;

export const SendEmailResponseSchema = z.object({
  providerMessageId: z.string(),
  providerThreadId: z.string(),
  // The outbound auto-match outcome (counterpart = primary `To`): the contact id on
  // an exactly-one match, else null (zero or many → unassigned).
  matchedContactId: refersTo('contact', z.uuid()).nullable(),
});
export type SendEmailResponse = z.infer<typeof SendEmailResponseSchema>;

export const CompanyMailboxStatusSchema = z.enum(['ACTIVE', 'SYNCING', 'EXPIRED']);
export type CompanyMailboxStatus = z.infer<typeof CompanyMailboxStatusSchema>;

export const CompleteCompanyMailboxConnectionResponseSchema = z.object({
  companyMailboxId: z.uuid(),
  email: z.string(),
  status: CompanyMailboxStatusSchema,
});
export type CompleteCompanyMailboxConnectionResponse = z.infer<typeof CompleteCompanyMailboxConnectionResponseSchema>;

export const CompanyMailboxViewSchema = z.object({
  id: z.uuid(),
  // Not `.email()`: stored addresses come from OAuth userinfo (BL-039).
  email: z.string(),
  provider: MailboxProviderSchema,
  // Present exactly when provider === 'IMAP' (IMM-1173.9): the row's provider line and the reconnect seed.
  imap: ImapConnectionSummarySchema.optional(),
  status: CompanyMailboxStatusSchema,
  connectedByMemberId: refersTo('member', z.uuid()),
  connectedAt: z.iso.datetime(),
});
export type CompanyMailboxView = z.infer<typeof CompanyMailboxViewSchema>;

export const ListCompanyMailboxesPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ListCompanyMailboxesResponseSchema = z.object({ items: z.array(CompanyMailboxViewSchema) });

export const CompanyMailboxPathParamsSchema = z.object({ workspaceId: z.uuid(), companyMailboxId: z.uuid() });
export const CompanyMailboxThreadPathParamsSchema = CompanyMailboxPathParamsSchema.extend({
  providerThreadId: z.string().min(1),
});

export const ListCompanyMailboxContactThreadsPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ListCompanyMailboxContactThreadsQuerySchema = z.object({ contactId: z.uuid() });
export const CompanyMailboxContactThreadSchema = z.object({
  companyMailboxId: z.uuid(),
  // null once the mailbox has been disconnected
  companyMailboxEmail: z.string().nullable(),
  providerThreadId: z.string(),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
  recipient: z.string().nullable(),
  direction: MailDirectionSchema,
  lastMessageAt: z.iso.datetime(),
  unreadCount: z.number().int(),
});
export type CompanyMailboxContactThread = z.infer<typeof CompanyMailboxContactThreadSchema>;
export const ListCompanyMailboxContactThreadsResponseSchema = z.object({
  items: z.array(CompanyMailboxContactThreadSchema),
});
